import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import {
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Home,
  Milk,
  Wheat,
  Package,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { useSidebarStore, type SidebarNavItem } from "./sidebar-store";
import { SidebarUser } from "./sidebar-user";
import { ScrollArea } from "@/components/ui/scroll-area";
import BrewbaseIcon from "@/components/icons/brewbase-icon";

export function MobileNav() {
  const { config } = useSidebarStore();

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40">
      <Sheet>
        <SheetTrigger asChild>
          <button className="w-full border-t border-olive-6 bg-olive-1/95 backdrop-blur px-3 py-2 flex items-center justify-between">
            <div className="min-w-0 text-left flex items-center gap-2">
              {config?.header ? (
                <>
                  {config.parent?.icon && (
                    <config.parent.icon className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div>
                    <div className="text-sm font-medium truncate">
                      {config.header.title}
                    </div>
                    {config.header.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">
                        {config.header.subtitle}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <BrewbaseIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">BrewBase</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 text-primary">
              <ChevronUp className="w-4 h-4" />
            </div>
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="h-[70vh] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>

          {/* Header */}
          <div className="px-3 py-3 border-b border-olive-6">
            {config?.parent ? (
              <SheetClose asChild>
                <Link
                  to={config.parent.href}
                  search={config.parent.search}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>{config.parent.label}</span>
                </Link>
              </SheetClose>
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <BrewbaseIcon className="w-4 h-4" />
                <span className="text-sm font-medium">BrewBase</span>
              </Link>
            )}

            {config?.header && (
              <div className="mt-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {config.header.title}
                    </div>
                    {config.header.subtitle && (
                      <div className="text-xs text-muted-foreground">
                        {config.header.subtitle}
                      </div>
                    )}
                  </div>
                  {config.header.actions && (
                    <div className="flex items-center gap-1 shrink-0">
                      {config.header.actions}
                    </div>
                  )}
                </div>
                {(config.header.status || config.header.meta) && (
                  <div className="flex items-center gap-2 mt-1.5">
                    {config.header.status}
                    {config.header.meta}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="h-[calc(70vh-8rem)]">
            <nav className="py-2">
              {config?.navigation.map((item) => (
                <MobileNavItem key={item.id} item={item} />
              ))}
              {!config && <DefaultMobileNav />}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 border-t border-olive-6 px-3 py-3 bg-background">
            {config?.footer ?? <SidebarUser />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MobileNavItem({ item }: { item: SidebarNavItem }) {
  const location = useLocation();

  if (item.type === "link") {
    const isActive =
      location.pathname === item.href ||
      location.pathname.startsWith(item.href + "/");
    const Icon = item.icon;

    return (
      <SheetClose asChild>
        <Link
          to={item.href}
          className={cn(
            "flex items-center justify-between px-3 py-2.5 text-sm transition-colors",
            isActive
              ? "bg-primary/10 text-primary font-medium"
              : "hover:bg-muted",
          )}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4" />}
            <span>{item.label}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      </SheetClose>
    );
  }

  if (item.type === "section") {
    const hasActiveChild = item.children.some(
      (child) =>
        child.type === "link" &&
        (location.pathname === child.href ||
          location.pathname.startsWith(child.href + "/")),
    );
    const Icon = item.icon;

    return (
      <div>
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-2 text-xs font-medium uppercase tracking-wider",
            hasActiveChild ? "text-primary" : "text-muted-foreground",
          )}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          <span>{item.label}</span>
        </div>
        <div className="ml-2">
          {item.children.map((child) => (
            <MobileNavItem key={child.id} item={child} />
          ))}
        </div>
      </div>
    );
  }

  if (item.type === "stages") {
    return (
      <div className="space-y-0.5">
        {item.items.map((stageItem) => {
          // Forks are punctuation, not destinations. On a sheet this narrow
          // they would be a row you cannot tap, so they are left to the desktop
          // sidebar, where they cost nothing.
          if (stageItem.kind === "fork") return null;
          const isActive = location.pathname === stageItem.href;
          return (
            <SheetClose key={stageItem.href} asChild>
              <Link
                to={stageItem.href}
                className={cn(
                  "flex items-center justify-between px-3 py-2.5 text-sm transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-muted",
                )}
              >
                <span>{stageItem.label}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            </SheetClose>
          );
        })}
      </div>
    );
  }

  return null;
}

function DefaultMobileNav() {
  const location = useLocation();

  const items = [
    { href: "/", label: "Home", icon: Home },
    { href: "/fermentations", label: "Fermentations", icon: Milk },
    { href: "/harvests", label: "Harvests", icon: Wheat },
    { href: "/batches", label: "Batches", icon: Package },
    { href: "/inventory", label: "Inventory", icon: Package },
  ];

  return (
    <>
      {items.map((item) => {
        const isActive =
          item.href === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(item.href);
        const Icon = item.icon;
        return (
          <SheetClose key={item.href} asChild>
            <Link
              to={item.href}
              className={cn(
                "flex items-center justify-between px-3 py-2.5 text-sm transition-colors",
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "hover:bg-muted",
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </Link>
          </SheetClose>
        );
      })}
    </>
  );
}
